import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AxiosInstance } from "axios";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { Input, Button, Empty, Spin, Modal, message, Tag } from "antd";
import { Plus, X } from "react-feather";
import "../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
import CustomDragAndDrop from "../../../CommonComponents/CustomDragAndDrop/CustomDragAndDrop";
import ShortcutsList, { ShortcutItem } from "./Shortcuts";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";
import {
  RenameEntityModal,
  type BundledTranslationChanges,
} from "../Translations/RenameEntityModal";
import {
  applyListEntityRename,
  applyListEntityTranslations,
  getListEntityBaseName,
  toListTranslationEditorData,
} from "../Translations/listTranslationData";

const ENTITY_TYPE = "ALL_SPORTS";

type BusyMap = Record<string, boolean>;

interface TokenData {
  AccessToken?: string;
}

interface ShortcutApiItem extends Omit<ShortcutItem, "entity_id"> {
  entity_id: string | number;
  logo?: string | null;
}

interface ShortcutsApiResponse {
  data?: {
    shortcuts?: ShortcutApiItem[];
    items?: ShortcutApiItem[];
  };
}

type ShortcutSearchType = "SPORT" | "LEAGUE" | (string & {});

interface ShortcutSearchApiItem {
  entity_id: string | number;
  item_id: string | number;
  item_name: string;
  item_type: ShortcutSearchType;
  country_tag?: string | null;
  country_name?: string | null;
  sport_name?: string | null;
  display_name?: string | null;
}

interface ShortcutSearchResponse {
  data?: ShortcutSearchApiItem[];
}

type ShortcutOption = {
  id?: string;
  entity_id?: string;
  item_id?: string;
  item_name: string;
  item_type: ShortcutSearchType;
  country_tag?: string | null;
  country_name?: string | null;
  sport_name?: string | null;
};

interface AddShortcutModalProps {
  onClose: () => void;
  onCreated: () => Promise<void>;
  PrivateAdminPanelInstance: AxiosInstance;
  shortcutsVersion: "v1" | "v2";
}

interface DeleteShortcutModalProps {
  onClose: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

interface ShortcutLogoModalProps {
  target: ShortcutItem;
  onClose: () => void;
  onSave: (payload: ShortcutLogoSavePayload) => Promise<void>;
  error?: string;
  saving?: boolean;
}

interface ShortcutLogoSavePayload {
  entityId: string;
  image?: string;
  resetToDefault: boolean;
}

const normalizeShortcut = (shortcut: ShortcutApiItem): ShortcutItem => ({
  ...shortcut,
  entity_id: String(shortcut.entity_id),
  image_link: shortcut.image_link ?? shortcut.logo ?? null,
});

const normalizeShortcutOption = (
  option: ShortcutSearchApiItem
): ShortcutOption => ({
  ...option,
  entity_id: String(option.entity_id),
  item_id: String(option.item_id),
  item_name: option.item_name ?? option.display_name,
});

const ShortcutsContainer: React.FC<{ shortcutsVersion?: "v1" | "v2" }> = ({
  shortcutsVersion = "v1",
}) => {
  const tokenString = localStorage.getItem("token");
  let token: TokenData | null = null;
  if (tokenString) {
    try {
      token = JSON.parse(tokenString) as TokenData;
    } catch {
      token = null;
    }
  }

  const accessToken = token?.AccessToken;
  const PrivateAdminPanelInstance = useMemo<AxiosInstance>(
    () => createPrivateAdminPanelInstance(accessToken) as AxiosInstance,
    [accessToken]
  );

  const [loading, setLoading] = useState<boolean>(false);
  /** Drives CSS table pulse for in-flight list mutations (toggle status, etc.). */
  const [tableMutationLoading, setTableMutationLoading] = useState(false);
  const tableMutationCountRef = useRef(0);
  const [rowBusy, setRowBusy] = useState<BusyMap>({});
  const [renameSaving, setRenameSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [items, setItems] = useState<ShortcutItem[]>([]);

  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<ShortcutItem | null>(null);
  const [logoTarget, setLogoTarget] = useState<ShortcutItem | null>(null);
  const [logoError, setLogoError] = useState<string>("");
  const [logoSaving, setLogoSaving] = useState<boolean>(false);
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const renameTranslationData = useMemo(
    () =>
      toListTranslationEditorData(
        renameTarget,
        renameTarget?.entity_id,
        renameTarget?.display_name ?? ""
      ),
    [renameTarget]
  );
  const renameInitialName = translationsEnabled
    ? getListEntityBaseName(renameTarget, renameTarget?.display_name ?? "")
    : (renameTarget?.display_name ?? "");

  const fetchShortcuts = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError("");
    try {
      const { data } =
        await PrivateAdminPanelInstance.get<ShortcutsApiResponse>(
          shortcutsVersion === "v1" ? API_ROUTES.GET_SHORTCUTS_ORDERING : API_ROUTES.GET_SHORTCUTS_ORDERING_V2
        );
      const shortcuts = shortcutsVersion === "v1" ? data?.data?.shortcuts?.map(normalizeShortcut) ?? [] : data?.data?.items?.map(normalizeShortcut) ?? [];
      setItems(shortcuts);
    } catch (e) {
      setError(getApiErrorMessage(e, AppStrings.FAILED_TO_LOAD_SHORTCUTS));
    } finally {
      setLoading(false);
    }
  }, [PrivateAdminPanelInstance, shortcutsVersion]);

  useEffect(() => {
    void fetchShortcuts();
  }, [fetchShortcuts]);

  const beginTableMutation = useCallback(() => {
    tableMutationCountRef.current += 1;
    setTableMutationLoading(true);
  }, []);

  const endTableMutation = useCallback(() => {
    tableMutationCountRef.current = Math.max(0, tableMutationCountRef.current - 1);
    if (tableMutationCountRef.current === 0) {
      setTableMutationLoading(false);
    }
  }, []);

  const isPanelLoading = loading || tableMutationLoading;

  const handleActiveToggle = useCallback(
    (record: ShortcutItem) => {
      const id = record.entity_id;
      if (rowBusy[id]) return;
      setRowBusy((m) => ({ ...m, [id]: true }));
      beginTableMutation();
      const prevActive = record.is_active;
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.entity_id === id ? { ...item, is_active: !prevActive } : item
        )
      );

      PrivateAdminPanelInstance.patch(shortcutsVersion === "v1" ? API_ROUTES.UPDATE_SHORTCUT_STATUS : API_ROUTES.UPDATE_SHORTCUT_STATUS_V2, {
        entity_id: id,
        is_active: !prevActive,
      }).then((response) => {
        message.success(
          getApiSuccessMessage(
            response,
            prevActive
              ? AppStrings.SHORTCUT_DEACTIVATED
              : AppStrings.SHORTCUT_ACTIVATED
          )
        );
      })
        .catch((e) => {
          setItems((prevItems) =>
            prevItems.map((item) =>
              item.entity_id === id ? { ...item, is_active: prevActive } : item
            )
          );
          setError(
            getApiErrorMessage(e, AppStrings.FAILED_TO_UPDATE_STATUS)
          );
        })
        .finally(() => {
          setRowBusy((m) => ({ ...m, [id]: false }));
          endTableMutation();
        });
    },
    [
      PrivateAdminPanelInstance,
      rowBusy,
      shortcutsVersion,
      beginTableMutation,
      endTableMutation,
    ]
  );

  const handleDelete = useCallback(() => {
    const id = showDeleteModal;
    if (!id) return;
    setRowBusy((m) => ({ ...m, [id]: true }));
    PrivateAdminPanelInstance.delete(shortcutsVersion === "v1" ? API_ROUTES.DELETE_SHORTCUT : API_ROUTES.DELETE_SHORTCUT_V2, {
      data: { entity_id: id },
    })
      .then((response) => {
        setItems((prevItems) =>
          prevItems.filter((item) => item.entity_id !== id)
        );
        message.success(
          getApiSuccessMessage(response, AppStrings.SHORTCUT_DELETED)
        );
        setShowDeleteModal(null);
      })
      .catch((e) => {
        setError(
          getApiErrorMessage(e, AppStrings.FAILED_TO_DELETE_SHORTCUT)
        );
      })
      .finally(() => {
        setRowBusy((m) => ({ ...m, [id]: false }));
      });
  }, [PrivateAdminPanelInstance, showDeleteModal, shortcutsVersion]);

  const applySavedShortcutTranslations = useCallback(
    ({ translation_key, translations }: BundledTranslationChanges) => {
      if (!renameTarget) return;
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.entity_id === renameTarget.entity_id
            ? applyListEntityTranslations(
                item,
                translations,
                "display_name",
                translation_key
              )
            : item
        )
      );
    },
    [renameTarget]
  );

  const handleRename = useCallback(
    (name: string, translationChanges?: BundledTranslationChanges) => {
      if (!renameTarget) return;
      const id = renameTarget.entity_id;
      setRenameSaving(true);
      const renameBody =
        shortcutsVersion === "v2"
          ? { entity_id: id, display_name: name }
          : { entity_id: id, updated_name: name };
      if (translationChanges) {
        Object.assign(renameBody, {
          translation_key: translationChanges.translation_key,
          translations: translationChanges.translations,
        });
      }
      return PrivateAdminPanelInstance.patch(
        shortcutsVersion === "v1"
          ? API_ROUTES.UPDATE_SHORTCUT_NAME
          : API_ROUTES.UPDATE_SHORTCUT_NAME_V2,
        renameBody
      )
        .then((response) => {
          setItems((prevItems) =>
            prevItems.map((item) => {
              if (item.entity_id !== id) return item;
              const renamed = applyListEntityRename(
                item,
                name,
                translationsEnabled,
                "display_name"
              );
              if (!translationChanges) return renamed;
              return applyListEntityTranslations(
                renamed,
                translationChanges.translations,
                "display_name",
                translationChanges.translation_key
              );
            })
          );
          setRenameTarget(null);
          message.success(
            getApiSuccessMessage(response, AppStrings.SHORTCUT_RENAMED)
          );
        })
        .catch((e) => {
          setError(getApiErrorMessage(e, AppStrings.FAILED_TO_RENAME));
        })
        .finally(() => {
          setRenameSaving(false);
        });
    },
    [PrivateAdminPanelInstance, renameTarget, shortcutsVersion, translationsEnabled]
  );

  const handleShortcutLogoSave = useCallback(
    async ({
      entityId,
      image,
      resetToDefault,
    }: ShortcutLogoSavePayload): Promise<void> => {
      if (shortcutsVersion !== "v2") return;

      setLogoSaving(true);
      setLogoError("");
      setRowBusy((m) => ({ ...m, [entityId]: true }));
      try {
        const response = resetToDefault
          ? await PrivateAdminPanelInstance.patch<{
              data?: ShortcutApiItem;
              message?: string;
            }>(API_ROUTES.RESET_SHORTCUT_LOGO_V2, {
              entity_id: entityId,
            })
          : await PrivateAdminPanelInstance.patch<{
              data?: ShortcutApiItem;
              message?: string;
            }>(API_ROUTES.UPDATE_SHORTCUT_LOGO_V2, {
              entity_id: entityId,
              image,
            });

        const updated = response.data?.data;
        setItems((prevItems) =>
          prevItems.map((item) => {
            if (item.entity_id !== entityId) return item;
            if (updated) {
              return normalizeShortcut({
                ...item,
                ...updated,
                entity_id: updated.entity_id ?? entityId,
              });
            }
            return {
              ...item,
              image_link: resetToDefault ? null : image ?? item.image_link,
            };
          })
        );
        setLogoTarget(null);
        setLogoError("");
        message.success(
          getApiSuccessMessage(
            response,
            resetToDefault
              ? AppStrings.SHORTCUT_ICON_RESET
              : AppStrings.SHORTCUT_ICON_UPDATED
          )
        );
      } catch (e) {
        setLogoError(
          getApiErrorMessage(
            e,
            resetToDefault
              ? AppStrings.FAILED_TO_RESET_SHORTCUT_ICON
              : AppStrings.FAILED_TO_UPDATE_SHORTCUT_ICON
          )
        );
      } finally {
        setLogoSaving(false);
        setRowBusy((m) => ({ ...m, [entityId]: false }));
      }
    },
    [PrivateAdminPanelInstance, shortcutsVersion]
  );

  const handleReorder = useCallback(
    (orderedIds: string[]) => {
      let previousItems: ShortcutItem[] = [];
      setItems((prevItems) => {
        previousItems = prevItems;
        const mapById = new Map(
          prevItems.map((item) => [item.entity_id, item])
        );
        const reordered = orderedIds
          .map((id) => mapById.get(id))
          .filter((item): item is ShortcutItem => Boolean(item));
        const leftovers = prevItems.filter(
          (item) => !orderedIds.includes(item.entity_id)
        );
        return [...reordered, ...leftovers];
      });
      beginTableMutation();
      const reorderRequest =
        shortcutsVersion === "v1"
          ? PrivateAdminPanelInstance.post(
              API_ROUTES.UPDATE_SHORTCUTS_ORDERING,
              { ordered_ids: orderedIds }
            )
          : PrivateAdminPanelInstance.patch(
              API_ROUTES.UPDATE_SHORTCUTS_ORDERING_V2,
              { ordered_ids: orderedIds }
            );
      reorderRequest
        .then((response) => {
          message.success(
            getApiSuccessMessage(
              response,
              AppStrings.SHORTCUTS_REORDERED_SUCCESSFULLY
            )
          );
        })
        .catch((e) => {
          setItems(previousItems);
          setError(getApiErrorMessage(e, AppStrings.FAILED_TO_REORDER));
        })
        .finally(() => {
          endTableMutation();
        });
    },
    [
      PrivateAdminPanelInstance,
      shortcutsVersion,
      beginTableMutation,
      endTableMutation,
    ]
  );

  const deleteBusy = showDeleteModal ? !!rowBusy[showDeleteModal] : false;

  return (
    <>
      <div className="head-controls">
        <div className="msb-actions">
          <Button
            type="primary"
            className="add-btn"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            <span>{AppStrings.ADD_SHORTCUT}</span>
          </Button>
        </div>
      </div>

      {error ? <div className="error-msg">{error}</div> : null}

      <div
        className={`msb-panel${isPanelLoading ? " msb-panel-loading" : ""}`}
        data-testid="shortcuts-table-panel"
        data-loading={isPanelLoading ? "true" : "false"}
      >
        <ShortcutsList
          items={items}
          onRename={(row) => setRenameTarget(row)}
          onEditLogo={
            shortcutsVersion === "v2"
              ? (row) => {
                  setLogoError("");
                  setLogoTarget(row);
                }
              : undefined
          }
          onToggleActive={handleActiveToggle}
          onDelete={(id) => setShowDeleteModal(id)}
          busyIds={rowBusy}
          onReorder={handleReorder}
        />
        {!isPanelLoading && items.length === 0 && (
          <div className="empty-container">
            <Empty description={AppStrings.NO_SHORTCUTS} />
          </div>
        )}
      </div>

      <Modal
        closable={false}
        open={showAddModal}
        onCancel={() => setShowAddModal(false)}
        footer={null}
        width={600}
      >
        <AddShortcutModal
          onClose={() => setShowAddModal(false)}
          onCreated={async () => {
            setShowAddModal(false);
            await fetchShortcuts();
          }}
          PrivateAdminPanelInstance={PrivateAdminPanelInstance}
          shortcutsVersion={shortcutsVersion}
        />
      </Modal>

      <Modal
        closable={false}
        open={!!showDeleteModal}
        onCancel={() => setShowDeleteModal(null)}
        footer={null}
        width={500}
      >
        <DeleteShortcutModal
          onClose={() => setShowDeleteModal(null)}
          onConfirm={handleDelete}
          busy={deleteBusy}
        />
      </Modal>

      <RenameEntityModal
        open={!!renameTarget}
        title={AppStrings.RENAME_SHORTCUT}
        nameLabel={AppStrings.DISPLAY_NAME}
        initialName={renameInitialName}
        entityKey={renameTarget?.translations?.key ?? renameTarget?.entity_id}
        initialTranslationData={renameTranslationData}
        translationsEnabled={translationsEnabled}
        bundleTranslationChanges={translationsEnabled}
        saving={renameSaving}
        onSave={handleRename}
        onTranslationsSaved={applySavedShortcutTranslations}
        onClose={() => setRenameTarget(null)}
      />

      <Modal
        className="shortcut-edit-icon-modal"
        closeIcon={<X className="close-icon" />}
        open={!!logoTarget}
        onCancel={() => {
          setLogoError("");
          setLogoTarget(null);
        }}
        footer={null}
        destroyOnHidden
      >
        {logoTarget ? (
          <ShortcutLogoModal
            key={logoTarget.entity_id}
            target={logoTarget}
            onClose={() => {
              setLogoError("");
              setLogoTarget(null);
            }}
            onSave={handleShortcutLogoSave}
            error={logoError}
            saving={logoSaving}
          />
        ) : null}
      </Modal>
    </>
  );
};

const AddShortcutModal: React.FC<AddShortcutModalProps> = ({
  onClose,
  onCreated,
  PrivateAdminPanelInstance,
  shortcutsVersion,
}) => {
  const [query, setQuery] = useState<string>("");
  const [options, setOptions] = useState<ShortcutOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<ShortcutOption | null>(null);
  const [error, setError] = useState<string>("");
  const [showResults, setShowResults] = useState<boolean>(false);

  const fetchOptions = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (q: string): Promise<void> =>
      new Promise((resolve) => {
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(async () => {
          if (!q) {
            setOptions([]);
            resolve();
            return;
          }
          setLoading(true);
          setError("");
          try {
            const autocompleteParams = {
              search_term: q,
              search_limit: 30,
              entity_type: ENTITY_TYPE,
              item_types: ["SPORT", "LEAGUE", "FEATURE"],
            };
            const { data } =
              shortcutsVersion === "v1"
                ? await PrivateAdminPanelInstance.post<ShortcutSearchResponse>(
                    API_ROUTES.GET_POTENTIAL_SHORTCUTS_AUTOCOMPLETE,
                    autocompleteParams
                  )
                : await PrivateAdminPanelInstance.get<ShortcutSearchResponse>(
                    API_ROUTES.GET_POTENTIAL_SHORTCUTS_AUTOCOMPLETE_V2,
                    { params: autocompleteParams }
                  );
            const normalized = data?.data?.map(normalizeShortcutOption) ?? [];
            setOptions(normalized);
          } catch (e) {
            setError(getApiErrorMessage(e, AppStrings.FAILED_TO_SEARCH));
          } finally {
            setLoading(false);
            resolve();
          }
        }, 250);
      });
  }, [PrivateAdminPanelInstance, shortcutsVersion]);

  useEffect(() => {
    void fetchOptions(query);
  }, [query, fetchOptions]);

  const handleCreate = useCallback(async (): Promise<void> => {
    if (!selected) return;
    const addRoute =
      shortcutsVersion === "v1"
        ? API_ROUTES.ADD_SHORTCUT
        : API_ROUTES.ADD_SHORTCUT_V2;

    const addBody =
      shortcutsVersion === "v1"
        ? { referent_id: selected.entity_id, referent_name: selected.item_name, referent_type: selected.item_type }
        : { item_id: selected.id, item_type: selected.item_type };
    try {
      await PrivateAdminPanelInstance.post(addRoute, addBody);
      await onCreated();
    } catch (e) {
      setError(getApiErrorMessage(e, AppStrings.FAILED_TO_CREATE));
    }
  }, [PrivateAdminPanelInstance, onCreated, selected, shortcutsVersion]);

  return (
    <div className="add-new-ip-panel">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">{AppStrings.ADD_SHORTCUT}</h2>
        </div>
        <X onClick={onClose} className="close-icon" />
      </div>
      <div className="divider" />
      <div className="msb-modal-body">
        <div className="autocomplete">
          <Input
            placeholder={AppStrings.SEARCH_SPORTS_OR_LEAGUES}
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              setShowResults(Boolean(value));
            }}
          />
          {showResults ? (
            <div className="results">
              {loading ? (
                <div className="center">
                  <Spin size="small" />
                </div>
              ) : (
                options.map((opt) => {
                  const showCountryTag =
                    opt.item_type === "LEAGUE" &&
                    (opt.country_tag || opt.country_name);
                  return (
                    <div
                      key={opt.entity_id}
                      className={`result-item ${
                        selected?.entity_id === opt.entity_id ? "selected" : ""
                      }`}
                      onClick={() => {
                        setSelected(opt);
                        setQuery(opt.item_name);
                        setShowResults(false);
                      }}
                    >
                      <div className="result-main">
                        <span className="result-name">{opt.item_name}</span>
                        {showCountryTag ? (
                          <Tag
                            color="purple"
                            className="result-tag result-country-tag"
                          >
                            {opt.country_tag || opt.country_name}
                          </Tag>
                        ) : null}
                        <Tag
                          color={opt.item_type === "LEAGUE" ? "blue" : "green"}
                          className="result-tag result-type-tag"
                        >
                          {opt.item_type}
                        </Tag>
                      </div>
                      {opt.sport_name ? (
                        <span className="result-sub">{opt.sport_name}</span>
                      ) : null}
                    </div>
                  );
                })
              )}
              {!loading && options.length === 0 && query && (
                <div className="empty-mini">{AppStrings.NO_RESULTS}</div>
              )}
            </div>
          ) : null}
        </div>
        {error ? <div className="error-msg">{error}</div> : null}
      </div>
      <div className="modal-footer">
        <button className="cancel-button" onClick={onClose}>
          {AppStrings.CLOSE}
        </button>
        <button
          className={`submit-button ${!selected ? "disabled" : ""}`}
          disabled={!selected}
          onClick={handleCreate}
        >
          {AppStrings.ADD_SHORTCUT}
        </button>
      </div>
    </div>
  );
};

const DeleteShortcutModal: React.FC<DeleteShortcutModalProps> = ({
  onClose,
  onConfirm,
  busy = false,
}) => {
  return (
    <div className="add-new-ip-panel">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">{AppStrings.DELETE_SHORTCUT}</h2>
        </div>
        <X onClick={onClose} className="close-icon" />
      </div>
      <div className="divider" />
      <div className="msb-modal-body">
        <p>{AppStrings.DELETE_SHORTCUT_CONFIRMATION}</p>
      </div>
      <div className="modal-footer">
        <button className="cancel-button" onClick={onClose} disabled={busy}>
          {AppStrings.CLOSE}
        </button>
        <button
          className={`submit-button delete-button ${busy ? "disabled" : ""}`}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? AppStrings.DELETING : AppStrings.DELETE}
        </button>
      </div>
    </div>
  );
};

const ShortcutLogoModal: React.FC<ShortcutLogoModalProps> = ({
  target,
  onClose,
  onSave,
  error,
  saving = false,
}) => {
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [selectedFileList, setSelectedFileList] = useState<File[]>([]);
  const [resetToDefault, setResetToDefault] = useState<boolean>(false);

  useEffect(() => {
    setSelectedImage("");
    setSelectedFileName("");
    setSelectedFileList([]);
    setResetToDefault(false);
  }, [target.entity_id]);

  const currentIcon = target.image_link || "";
  const previewSrc = selectedImage || "";
  const canSave = resetToDefault || Boolean(selectedImage);

  const handleFileUpload = (base64: string, file?: File): void => {
    setSelectedImage(base64);
    setSelectedFileName(file?.name ?? "");
    setResetToDefault(false);
  };

  const handleRemove = (): void => {
    setSelectedImage("");
    setSelectedFileName("");
    setSelectedFileList([]);
  };

  const handleSave = (): void => {
    if (!canSave || saving) return;
    void onSave({
      entityId: target.entity_id,
      image: selectedImage || undefined,
      resetToDefault,
    });
  };

  return (
    <div className="edit-game-icon-content shortcut-edit-icon-content">
      <h2>{AppStrings.EDIT_SHORTCUT_ICON}</h2>
      <div className="shortcut-current-icon-section">
        <span className="shortcut-current-icon-label">
          {AppStrings.CURRENT_ICON}
        </span>
        <div className="shortcut-current-icon-preview">
          {currentIcon ? (
            <img
              src={currentIcon}
              alt={AppStrings.ICON_ALT.replace(
                "{name}",
                target.display_name
              )}
            />
          ) : (
            <span>{AppStrings.NO_ICON}</span>
          )}
        </div>
      </div>

      <div className="drag-drop-container">
        <CustomDragAndDrop
          onFileUpload={handleFileUpload}
          onRemove={handleRemove}
          maxSizeMB={1}
          description={AppStrings.SHORTCUT_ICON_UPLOAD_INSTRUCTIONS}
          allowedMimeTypes={["image/png", "image/svg+xml", "image/webp"]}
          allowedExtensions={[".png", ".svg", ".webp"]}
          allowedTypesMessage={AppStrings.ONLY_SHORTCUT_ICON_FILES_ALLOWED}
          fileList={selectedFileList}
          onFileListChange={setSelectedFileList}
        />
      </div>

      {previewSrc ? (
        <div className="icon-preview shortcut-icon-preview">
          <img
            src={previewSrc}
            alt={AppStrings.ICON_ALT.replace("{name}", target.display_name)}
          />
          {selectedFileName ? (
            <span className="shortcut-logo-modal-file">{selectedFileName}</span>
          ) : null}
        </div>
      ) : null}

      {resetToDefault ? (
        <div className="shortcut-reset-notice">
          {AppStrings.DEFAULT_ICON_WILL_BE_RESTORED}
        </div>
      ) : null}

      <button
        type="button"
        className="shortcut-reset-link"
        onClick={() => {
          setSelectedImage("");
          setSelectedFileName("");
          setSelectedFileList([]);
          setResetToDefault(true);
        }}
        disabled={saving}
      >
        {AppStrings.RESET_TO_DEFAULT}
      </button>

      {error ? <div className="error-msg">{error}</div> : null}

      <div className="modal-footer-buttons">
        <button className="cancel-button" onClick={onClose} disabled={saving}>
          {AppStrings.CANCEL}
        </button>
        <button
          className="save-icon-button"
          disabled={!canSave || saving}
          onClick={handleSave}
        >
          {saving ? AppStrings.SAVING : AppStrings.SAVE_ICON}
        </button>
      </div>
    </div>
  );
};

export default ShortcutsContainer;
